/**
 * Thin wrapper over Wistia's JavaScript Player API.
 *
 * The API works against iframe embeds as long as Wistia's E-v1 script is loaded
 * on the parent page: it hands out a player handle through the `window._wq`
 * queue and talks to the iframe over postMessage. We use it to tell whether an
 * agent actually watched a training video, rather than trusting a timer.
 */

export interface WistiaVideo {
  bind(event: string, handler: (...args: unknown[]) => void): void;
  unbind(event: string, handler?: (...args: unknown[]) => void): void;
  /**
   * Fraction of the video watched, 0–1. Wistia derives this from unique seconds
   * watched, so seeking past a section never counts it as seen — which is
   * exactly the property we need for "watched it all the way".
   */
  percentWatched(): number;
}

declare global {
  interface Window {
    _wq?: unknown[];
  }
}

const EMBED_SCRIPT_SRC = "https://fast.wistia.net/assets/external/E-v1.js";

let scriptPromise: Promise<void> | null = null;

/** Loads E-v1.js once per page. Rejects if it's blocked or fails to load. */
export function loadWistiaEmbedScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${EMBED_SCRIPT_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = EMBED_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("Wistia player script failed to load")));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

// A _wq matcher can't be withdrawn once queued, so each media id gets exactly
// one matcher for the life of the page and we swap the listener behind it.
const listeners = new Map<string, (video: WistiaVideo) => void>();
const matched = new Set<string>();

/**
 * Calls `listener` with the player for `mediaId` once it's ready — immediately
 * if Wistia has already built it. Returns an unsubscribe function.
 */
export function onWistiaVideoReady(
  mediaId: string,
  listener: (video: WistiaVideo) => void
): () => void {
  listeners.set(mediaId, listener);
  if (!matched.has(mediaId)) {
    matched.add(mediaId);
    window._wq = window._wq || [];
    window._wq.push({
      [mediaId]: (video: WistiaVideo) => listeners.get(mediaId)?.(video),
    });
  }
  return () => {
    if (listeners.get(mediaId) === listener) listeners.delete(mediaId);
  };
}
